import { supabase } from './supabase';
import type { WeddingTimelineEvent, WeddingVendor } from '../types/dayof';

export async function getTimelineEvents(registryId?: string): Promise<WeddingTimelineEvent[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    let query = supabase
      .from('wedding_timeline_events')
      .select('*')
      .eq('user_id', user.id)
      .order('time', { ascending: true });
    if (registryId) query = query.eq('registry_id', registryId);

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching timeline events:', error);
    throw error;
  }
}

export async function addTimelineEvent(event: Omit<WeddingTimelineEvent, 'id'>, registryId?: string): Promise<WeddingTimelineEvent | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const insertData: Record<string, unknown> = {
      ...event,
      user_id: user.id
    };
    if (registryId) insertData.registry_id = registryId;

    const { data, error } = await supabase
      .from('wedding_timeline_events')
      .insert([insertData])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding timeline event:', error);
    throw error;
  }
}

export async function updateTimelineEvent(id: string, updates: Partial<WeddingTimelineEvent>): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const { error } = await supabase
      .from('wedding_timeline_events')
      .update(updates)
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating timeline event:', error);
    throw error;
  }
}

export async function deleteTimelineEvent(id: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const { error } = await supabase
      .from('wedding_timeline_events')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting timeline event:', error);
    throw error;
  }
}

export async function getVendors(registryId?: string): Promise<WeddingVendor[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    let query = supabase
      .from('wedding_vendors')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });
    if (registryId) query = query.eq('registry_id', registryId);

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching vendors:', error);
    throw error;
  }
}

export async function addVendor(vendor: Omit<WeddingVendor, 'id' | 'user_id' | 'created_at'>, registryId?: string): Promise<WeddingVendor | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const insertData: Record<string, unknown> = {
      ...vendor,
      user_id: user.id
    };
    if (registryId) insertData.registry_id = registryId;

    const { data, error } = await supabase
      .from('wedding_vendors')
      .insert([insertData])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding vendor:', error);
    throw error;
  }
}

export async function updateVendor(id: string, updates: Partial<WeddingVendor>): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const { error } = await supabase
      .from('wedding_vendors')
      .update(updates)
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating vendor:', error);
    throw error;
  }
}

export async function deleteVendor(id: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const { error } = await supabase
      .from('wedding_vendors')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting vendor:', error);
    throw error;
  }
}

export async function loadTimelineTemplate(
  items: { time: string; event: string; status: string; duration?: number; location?: string; responsible?: string[]; notes?: string }[],
  registryId?: string
): Promise<WeddingTimelineEvent[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User must be authenticated');

  const rows = items.map(item => ({
    ...item,
    user_id: user.id,
    ...(registryId ? { registry_id: registryId } : {})
  }));
  const { data, error } = await supabase
    .from('wedding_timeline_events')
    .insert(rows)
    .select();

  if (error) throw error;
  return (data || []).sort((a: WeddingTimelineEvent, b: WeddingTimelineEvent) => a.time.localeCompare(b.time));
}

export async function loadVendorTemplate(
  items: { name: string; contact: string; role: string; arrival_time: string; setup_time?: string; notes?: string; status: string }[],
  registryId?: string
): Promise<WeddingVendor[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User must be authenticated');

  const rows = items.map(item => ({
    ...item,
    user_id: user.id,
    ...(registryId ? { registry_id: registryId } : {})
  }));
  const { data, error } = await supabase
    .from('wedding_vendors')
    .insert(rows)
    .select();

  if (error) throw error;
  return data || [];
}

export async function getUserRegistryDate(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('registries')
      .select('event_date')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error || !data) return null;
    return data.event_date;
  } catch (error) {
    console.error('Error fetching registry date:', error);
    return null;
  }
}

export async function getUserGuestList(registryId?: string): Promise<string[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    let rid = registryId;
    if (!rid) {
      const { data: registry, error: registryError } = await supabase
        .from('registries')
        .select('id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (registryError || !registry) return [];
      rid = registry.id;
    }

    const { data: guestTables, error: guestError } = await supabase
      .from('guest_tables')
      .select('guest_name')
      .eq('registry_id', rid)
      .order('guest_name', { ascending: true });

    if (guestError) throw guestError;
    return guestTables?.map(guest => guest.guest_name) || [];
  } catch (error) {
    console.error('Error fetching guest list:', error);
    return [];
  }
}
